import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard,
  Package,
  Users,
  Calculator,
  ShoppingCart,
  Truck,
  ClipboardList,
  BarChart3,
  Store,
  Sun,
  Moon,
  CheckCircle,
  ArrowRight,
  Shield,
  Zap,
  Globe,
  Printer,
  Bell,
  MessageSquare,
  Wallet,
  WifiOff,
  Brain,
  Mail,
  BadgeCheck,
  Gift,
  Banknote,
  Phone,
  Send,
  X,
  MapPin,
  FileText,
  Loader2,
  CheckCircle2,
  Lightbulb,
  Star,
  Menu,
  Play,
  ChevronDown
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { apiClient } from '../services/apiClient';

const LandingPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { getSetting } = useSettings();
  const SITE_NAME = 'NextGen - Shop'; // Frontend always uses NextGen - Shop
  const [isScrolled, setIsScrolled] = useState(false);
  const [logoImageFailed, setLogoImageFailed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'form'>('contact');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isLegalModalClosing, setIsLegalModalClosing] = useState(false);
  const [isFeatureRequestModalOpen, setIsFeatureRequestModalOpen] = useState(false);
  const [isFeatureModalClosing, setIsFeatureModalClosing] = useState(false);
  const [featureFormData, setFeatureFormData] = useState({ name: '', email: '', feature: '' });
  const [featureFormStatus, setFeatureFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [featureFormMessage, setFeatureFormMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // SEO: Update document title and meta tags for landing page
    document.title = 'NextGen - Shop - Best POS Software & Inventory Management System';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Affordable web POS software and retail management system for shops in Pakistan. Digital point of sale, inventory management, sales tracking & cloud POS system. Start free trial today!');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'pos software, pos system, online pos, digital pos, web pos, smart pos, inventory management system, retail management, shop software, retail software, cloud pos system, digital pos system, point of sale software, retail pos, best pos software pakistan, inventory software pakistan, shop management software, billing software pakistan, free pos software, cloud point of sale system');
    }
  }, []);

  const handleOpenContactModal = () => {
    setIsContactModalOpen(true);
    setActiveTab('contact');
    setFormStatus('idle');
    setFormMessage('');
  };

  const handleCloseContactModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalClosing(false);
      setIsContactModalOpen(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setFormStatus('idle');
    }, 150);
  };

  const handleOpenPrivacyModal = () => setIsPrivacyModalOpen(true);
  const handleOpenTermsModal = () => setIsTermsModalOpen(true);
  
  const handleCloseLegalModal = () => {
    setIsLegalModalClosing(true);
    setTimeout(() => {
      setIsLegalModalClosing(false);
      setIsPrivacyModalOpen(false);
      setIsTermsModalOpen(false);
    }, 150);
  };

  const handleOpenFeatureRequestModal = () => {
    setIsFeatureRequestModalOpen(true);
    setFeatureFormStatus('idle');
    setFeatureFormMessage('');
  };

  const handleCloseFeatureRequestModal = () => {
    setIsFeatureModalClosing(true);
    setTimeout(() => {
      setIsFeatureModalClosing(false);
      setIsFeatureRequestModalOpen(false);
      setFeatureFormData({ name: '', email: '', feature: '' });
      setFeatureFormStatus('idle');
    }, 150);
  };

  const handleFeatureFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeatureFormStatus('loading');
    setFeatureFormMessage('');
    
    try {
      await apiClient.post<{ success: boolean; message: string }>('/contact', {
        name: featureFormData.name,
        email: featureFormData.email,
        phone: '',
        message: `[FEATURE REQUEST]\n\n${featureFormData.feature}`
      });
      setFeatureFormStatus('success');
      setFeatureFormMessage('Thank you for your suggestion! We review all feature requests.');
      setFeatureFormData({ name: '', email: '', feature: '' });
    } catch (error: any) {
      setFeatureFormStatus('error');
      setFeatureFormMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormMessage('');
    
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/contact', formData);
      setFormStatus('success');
      setFormMessage(response.message || 'Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error: any) {
      setFormStatus('error');
      setFormMessage(error.response?.data?.message || 'Something went wrong. Please try again.');
    }
  };

  // Helper function to get the correct logo URL
  const getLogoUrl = (): string => {
    const logoPath = getSetting('site_logo');
    if (!logoPath) return '';
    
    if (logoPath.startsWith('http')) {
      return logoPath;
    }
    
    if (logoPath.includes('/storage/')) {
      const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const baseUrl = apiBase.replace('/api', '');
      return `${baseUrl}${logoPath}`;
    }
    
    const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
    const baseUrl = apiBase.replace('/api', '');
    return `${baseUrl}/storage/${logoPath}`;
  };

  const features = [
    {
      icon: LayoutDashboard,
      title: 'Smart POS Dashboard',
      description: 'Real-time analytics dashboard with sales tracking, profit monitoring, and business insights for your digital POS system.'
    },
    {
      icon: Package,
      title: 'Inventory Management System',
      description: 'Complete stock management with low-stock alerts, product categories, and barcode support for retail inventory control.'
    },
    {
      icon: Calculator,
      title: 'Point of Sale (POS)',
      description: 'Fast and intuitive web POS system with barcode scanning, walk-in customers, and multiple payment methods.'
    },
    {
      icon: Users,
      title: 'Customer Management',
      description: 'Build customer relationships with detailed profiles, purchase history, dues tracking, and payment management.'
    },
    {
      icon: BadgeCheck,
      title: 'Staff & Access Control',
      description: 'Multi-user retail software with role-based permissions and secure staff management for your shop.'
    },
    {
      icon: ShoppingCart,
      title: 'Sales & Returns Tracking',
      description: 'Complete sales management with invoicing, payment tracking, and easy product returns processing.'
    },
    {
      icon: Truck,
      title: 'Supplier Management',
      description: 'Streamline procurement with supplier profiles, purchase history, and payment tracking in one place.'
    },
    {
      icon: ClipboardList,
      title: 'Purchase Order System',
      description: 'Create and manage purchase orders, receive inventory items, and track supplier payments effortlessly.'
    },
    {
      icon: BarChart3,
      title: 'Business Reports & Analytics',
      description: 'Comprehensive retail reports for sales, inventory, finances, and customer analytics to grow your business.'
    },
    {
      icon: Printer,
      title: 'Thermal Receipt Printing',
      description: 'Professional receipt printing with built-in thermal printer support for quick POS transactions.'
    },
    {
      icon: Wallet,
      title: 'Dues & Payment Tracking',
      description: 'Track customer dues, partial payments, and outstanding balances with automated reminders.'
    },
    {
      icon: MessageSquare,
      title: 'Customer Support System',
      description: 'Built-in support ticket system for issue reporting and real-time resolution tracking.'
    }
  ];

  const highlights = [
    {
      icon: Shield,
      title: 'Secure Cloud POS',
      description: 'Enterprise-grade security with encrypted data and automatic daily backups for your retail data.'
    },
    {
      icon: Zap,
      title: 'Fast Online POS',
      description: 'Lightning-fast web POS system optimized for quick transactions and instant inventory updates.'
    },
    {
      icon: Globe,
      title: 'Web-Based Access',
      description: 'Access your shop software from anywhere. Cloud point of sale system works on any device with a browser.'
    },
    {
      icon: Bell,
      title: 'Smart Inventory Alerts',
      description: 'Get notified about low stock levels, expiring products, and pending customer payments automatically.'
    }
  ];

  const upcomingFeatures = [
    {
      icon: WifiOff,
      title: 'Offline-First Architecture',
      description: 'Work without internet - data syncs automatically when you reconnect.',
      status: 'Coming Soon'
    },
    {
      icon: Brain,
      title: 'AI-Powered Insights',
      description: 'Get smart recommendations and predictions for your business.',
      status: 'In Development'
    },
    {
      icon: Mail,
      title: 'Scheduled Reports',
      description: 'Automatic daily, weekly, or monthly reports delivered to your inbox.',
      status: 'Coming Soon'
    }
  ];

  // Pricing toggle state
  const [pricingMode, setPricingMode] = useState<'yearly' | 'monthly'>('yearly');

  // FAQ accordion state
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const faqItems = [
    {
      question: 'What is NextGen - Shop POS software?',
      answer: 'NextGen - Shop is a complete digital POS system and inventory management software designed for retail shops in Pakistan. It\'s a cloud-based point of sale solution that helps you manage sales, track inventory, handle customer dues, and generate business reports — all from your browser.'
    },
    {
      question: 'Is this a cloud POS system or do I need to install it?',
      answer: 'NextGen - Shop is a web-based online POS system. No installation needed! Simply log in from any device with a browser. Your data is securely stored on our cloud servers with automatic daily backups. It\'s a true cloud point of sale system accessible from anywhere.'
    },
    {
      question: 'What features does your inventory management system include?',
      answer: 'Our retail inventory management system includes stock tracking, low-stock alerts, product categories, barcode support, supplier management, purchase orders, and detailed inventory reports. You can manage unlimited products with custom fields for your specific business needs.'
    },
    {
      question: 'Is there a free POS software trial available?',
      answer: 'Yes! We offer a 14-day free trial with full access to all features. Try our free point of sale software with no credit card required. Experience the complete digital POS system before making any commitment.'
    },
    {
      question: 'How much does your POS system cost in Pakistan?',
      answer: 'Our affordable POS software pricing is designed for Pakistani shops: ₨30,000 one-time license fee (yours forever) + ₨12,000/year for web hosting. Or pay monthly at ₨3,500/month. This is the best value point of sale software for retail businesses in Pakistan.'
    },
    {
      question: 'Does your smart POS support thermal receipt printing?',
      answer: 'Yes! Our digital POS system has built-in thermal printer support. Print professional receipts instantly at your retail counter. Compatible with most thermal receipt printers available in Pakistan.'
    },
    {
      question: 'What types of shops can use this retail management system?',
      answer: 'Our retail POS software is perfect for general stores, grocery shops, electronics stores, clothing boutiques, pharmacies, hardware stores, mobile shops, and any retail business in Pakistan. The system is customizable for different shop types.'
    },
    {
      question: 'Can I track customer dues and payments?',
      answer: 'Yes! Our POS system includes complete dues management. Track customer balances, partial payments, payment history, and outstanding amounts. Generate customer statements and manage credit sales easily — essential for shops in Pakistan.'
    },
    {
      question: 'Can I use this retail software on multiple devices?',
      answer: 'Absolutely! Our web POS works on any device — desktop, laptop, tablet, or mobile. Access your shop software from multiple devices simultaneously. Perfect for retail businesses with multiple counters or staff members.'
    },
    {
      question: 'Is my shop data secure on your cloud POS system?',
      answer: 'Absolutely! We use enterprise-grade security with SSL encryption, secure servers, and automatic daily backups. Your retail data is protected with the same security standards used by banks. We guarantee 99.9% uptime for your online POS system.'
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      theme === 'dark' ? 'bg-slate-900' : 'bg-white'
    }`}>
      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm lg:hidden animate-fade-in"
          style={{ zIndex: 99998 }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Aside - Glassmorphism */}
      <aside 
        className={`fixed inset-y-0 left-0 w-72 transform transition-all duration-300 ease-in-out lg:hidden flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          zIndex: 99999,
          backgroundColor: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: theme === 'dark' ? '4px 0 30px rgba(0, 0, 0, 0.3)' : '4px 0 30px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-6 border-b" style={{
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
        }}>
          <span className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Menu
          </span>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className={`p-2 rounded-lg transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2">
          <a
            href="#features"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Star className="w-5 h-5 mr-3" />
            Explore Features
          </a>
          <a
            href="#why-us"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Shield className="w-5 h-5 mr-3" />
            Why Choose Us
          </a>
          <a
            href="#pricing"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Banknote className="w-5 h-5 mr-3" />
            Pricing & Plans
          </a>
        </nav>

        {/* Action Buttons */}
        <div className="mt-auto p-4 space-y-3 border-t" style={{
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
        }}>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              window.open('/login?demo=true', '_blank');
            }}
            className={`w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-all duration-200 border-2 ${
              theme === 'dark'
                ? 'border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 hover:border-indigo-500'
                : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400'
            }`}
          >
            <Play className="w-4 h-4 mr-2" />
            Try Demo
          </button>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              handleOpenContactModal();
            }}
            className="w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Get Started
          </button>
        </div>
      </aside>

      {/* Navigation */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: isScrolled 
            ? (theme === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.85)')
            : (theme === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)'),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: isScrolled 
            ? (theme === 'dark' ? '0 4px 30px rgba(0, 0, 0, 0.3)' : '0 4px 30px rgba(0, 0, 0, 0.1)')
            : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 gap-2">
            {/* Logo */}
            <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0 flex-1 sm:flex-initial">
              {getLogoUrl() ? (
                <img 
                  src={getLogoUrl()} 
                  alt={SITE_NAME}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    setLogoImageFailed(true);
                  }}
                />
              ) : null}
              {(!getLogoUrl() || logoImageFailed) && (
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  theme === 'dark' ? 'bg-indigo-600' : 'bg-indigo-600'
                }`}>
                  <Store className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
              )}
              <div className="flex flex-col min-w-0">
                <span className={`text-base sm:text-xl font-bold leading-tight truncate ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {SITE_NAME}
                </span>
                <span className={`text-xs font-medium line-clamp-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Inventory & POS
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6">
              <a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); }} className={`text-sm font-medium transition-colors cursor-pointer ${
                theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}>Explore Features</a>
              <a href="#why-us" onClick={(e) => { e.preventDefault(); document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' }); }} className={`text-sm font-medium transition-colors cursor-pointer ${
                theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}>Why Choose Us</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }} className={`text-sm font-medium transition-colors cursor-pointer ${
                theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}>Pricing & Plans</a>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-1.5 sm:space-x-2 gap-1">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-1.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-slate-800'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>

              <button
                onClick={toggleTheme}
                className={`p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex-shrink-0 ${
                  theme === 'dark'
                    ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={16} className="sm:w-5 sm:h-5" /> : <Moon size={16} className="sm:w-5 sm:h-5" />}
              </button>
              <button
                onClick={() => window.open('/login?demo=true', '_blank')}
                className={`hidden md:inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-semibold rounded-lg sm:rounded-xl transition-all duration-300 flex-shrink-0 gap-1 ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                Try Demo
              </button>
              <button
                onClick={handleOpenContactModal}
                className="hidden md:inline-flex items-center px-3 py-1.5 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl flex-shrink-0 gap-1 sm:gap-2"
              >
                Get Started
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm lg:hidden animate-fade-in"
          style={{ zIndex: 99998 }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Aside - Glassmorphism */}
      <aside 
        className={`fixed inset-y-0 left-0 w-72 transform transition-all duration-300 ease-in-out lg:hidden flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          zIndex: 99999,
          backgroundColor: theme === 'dark' ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRight: theme === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: theme === 'dark' ? '4px 0 30px rgba(0, 0, 0, 0.3)' : '4px 0 30px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-6 border-b" style={{
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
        }}>
          <span className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Menu
          </span>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className={`p-2 rounded-lg transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-2">
          <a
            href="#features"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Star className="w-5 h-5 mr-3" />
            Explore Features
          </a>
          <a
            href="#why-us"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('why-us')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Shield className="w-5 h-5 mr-3" />
            Why Choose Us
          </a>
          <a
            href="#pricing"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className={`flex items-center px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700/50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
            }`}
          >
            <Banknote className="w-5 h-5 mr-3" />
            Pricing & Plans
          </a>
        </nav>

        {/* Action Buttons */}
        <div className="mt-auto p-4 space-y-3 border-t" style={{
          borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
        }}>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              window.open('/login?demo=true', '_blank');
            }}
            className={`w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-all duration-200 border-2 ${
              theme === 'dark'
                ? 'border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 hover:border-indigo-500'
                : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400'
            }`}
          >
            <Play className="w-4 h-4 mr-2" />
            Try Demo
          </button>
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              handleOpenContactModal();
            }}
            className="w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Get Started
          </button>
        </div>
      </aside>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-6 ${
              theme === 'dark' 
                ? 'bg-indigo-900/50 text-indigo-300 border border-indigo-800' 
                : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
            }`}>
              <Gift className="w-3.5 h-3.5 mr-1.5" />
              #1 POS Software & Inventory Management System in Pakistan
            </div>
            
            <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Best <span className="text-indigo-600">Digital POS System</span> for Your Retail Business
            </h1>
            
            <p className={`text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Affordable web POS software with complete inventory management, sales tracking, customer management & financial reports. Cloud-based point of sale system designed for Pakistani shops and retail businesses.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="#features"
                onClick={(e) => { e.preventDefault(); document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' }); }}
                className={`w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-semibold rounded-xl transition-all duration-300 border-2 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
                  theme === 'dark'
                    ? 'border-slate-700 text-gray-300 hover:bg-slate-800 hover:text-white hover:border-slate-600'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Lightbulb className="mr-2 w-5 h-5" />
                Explore Features
              </a>
              <button
                onClick={() => window.open('/login?demo=true', '_blank')}
                className={`w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-semibold rounded-xl transition-all duration-300 border-2 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
                  theme === 'dark'
                    ? 'border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 hover:border-indigo-500'
                    : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400'
                }`}
              >
                <Play className="mr-2 w-5 h-5" />
                Try Demo
              </button>
              <button
                onClick={handleOpenContactModal}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-16 sm:mt-20 relative">
            <div className={`rounded-2xl sm:rounded-3xl p-2 sm:p-3 ${
              theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-gray-100 border border-gray-200'
            }`}>
              <div className={`rounded-xl sm:rounded-2xl overflow-hidden ${
                theme === 'dark' ? 'bg-slate-900' : 'bg-white'
              }`}>
                {/* Mock Dashboard Header */}
                <div className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b ${
                  theme === 'dark' ? 'border-slate-800' : 'border-gray-100'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      theme === 'dark' ? 'bg-indigo-600' : 'bg-indigo-600'
                    }`}>
                      <Store className="w-4 h-4 text-white" />
                    </div>
                    <span className={`font-semibold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>Dashboard</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-20 sm:w-32 h-8 rounded-lg ${
                      theme === 'dark' ? 'bg-slate-800' : 'bg-gray-100'
                    }`}></div>
                    <div className={`w-8 h-8 rounded-full ${
                      theme === 'dark' ? 'bg-slate-800' : 'bg-gray-200'
                    }`}></div>
                  </div>
                </div>
                
                {/* Mock Dashboard Content */}
                <div className="p-4 sm:p-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    {[
                      { label: 'Total Sales', value: '₨125,430', color: 'blue' },
                      { label: 'Transactions', value: '234', color: 'green' },
                      { label: 'Customers', value: '1,247', color: 'purple' },
                      { label: 'Products', value: '856', color: 'orange' }
                    ].map((stat, index) => (
                      <div key={index} className={`p-3 sm:p-4 rounded-xl border ${
                        theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
                      }`}>
                        <p className={`text-xs sm:text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>{stat.label}</p>
                        <p className={`text-lg sm:text-xl font-bold mt-1 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>{stat.value}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Chart Placeholder */}
                  <div className={`h-40 sm:h-52 rounded-xl flex items-end justify-around px-4 py-6 ${
                    theme === 'dark' ? 'bg-slate-800' : 'bg-gray-50'
                  }`}>
                    {[65, 40, 85, 55, 90, 45, 70].map((height, idx) => (
                      <div key={idx} className="flex flex-col items-center space-y-2">
                        <div className="flex items-end space-x-1">
                          <div 
                            className={`w-3 sm:w-4 rounded-t ${
                              theme === 'dark' ? 'bg-indigo-500' : 'bg-indigo-400'
                            }`}
                            style={{ height: `${height}px` }}
                          />
                          <div 
                            className={`w-3 sm:w-4 rounded-t ${
                              theme === 'dark' ? 'bg-green-500' : 'bg-green-400'
                            }`}
                            style={{ height: `${height * 0.6}px` }}
                          />
                        </div>
                        <span className={`text-xs ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${
        theme === 'dark' ? 'bg-slate-800/50' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Complete POS Software & Retail Management Features
            </h2>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Everything you need in a digital point of sale system — inventory management, sales tracking, and business analytics in one powerful platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-2xl border transition-all duration-300 hover:shadow-xl ${
                    theme === 'dark'
                      ? 'bg-slate-800/50 border-slate-700 hover:border-indigo-500/50'
                      : 'bg-white border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  {/* Gradient accent line at top */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <div className="p-5 sm:p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110 ${
                        theme === 'dark' 
                          ? 'bg-gradient-to-br from-indigo-600/20 to-purple-600/20 text-indigo-400 border border-indigo-500/30' 
                          : 'bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600 border border-indigo-100'
                      }`}>
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-base font-semibold mb-1.5 ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {feature.title}
                        </h3>
                        <p className={`text-sm leading-relaxed ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${
        theme === 'dark' ? 'bg-slate-900' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Why Choose Our Cloud POS System?
            </h2>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Best POS software for retail shops in Pakistan. Our smart POS platform combines 
              powerful inventory management with an intuitive interface. Perfect for small shops 
              and growing retail businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {highlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className={`p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border transition-all duration-300 hover:shadow-lg ${
                  theme === 'dark' ? 'bg-slate-800/50 border-slate-700 hover:border-indigo-500/50' : 'bg-white border-gray-200 hover:border-indigo-300'
                }`}>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 mb-3 sm:mb-4 ${
                    theme === 'dark' ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <h3 className={`font-semibold mb-1 sm:mb-2 text-sm sm:text-base ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {item.title}
                  </h3>
                  <p className={`text-xs sm:text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coming Soon Features Section */}
      <section className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${
        theme === 'dark' ? 'bg-slate-900 border-b border-slate-800' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>Coming Soon</h2>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              We're constantly innovating to bring you the best features. Here's what we're working on to make your business even more powerful.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {upcomingFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className={`p-5 sm:p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg group ${
                  theme === 'dark' ? 'bg-slate-800/50 border-slate-700 hover:border-indigo-500/50' : 'bg-white border-gray-200 hover:border-indigo-300'
                }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      theme === 'dark' ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                      theme === 'dark' 
                        ? 'bg-indigo-900/50 text-indigo-300' 
                        : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {feature.status}
                    </span>
                  </div>
                  <h3 className={`font-semibold mb-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>{feature.title}</h3>
                  <p className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <p className={`text-sm mb-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Have a feature request or suggestion?
            </p>
            <button 
              onClick={handleOpenFeatureRequestModal}
              className="inline-flex items-center px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
            >
              Suggest a Feature
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${
        theme === 'dark' ? 'bg-slate-800/50' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Affordable POS Software Pricing
            </h2>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto mb-8 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Best value point of sale software for Pakistani shops. One complete package with everything included — choose how you want to pay.
            </p>

            {/* Payment Toggle */}
            <div className="inline-flex items-center p-1.5 rounded-xl bg-slate-100 dark:bg-slate-800">
              <button
                onClick={() => setPricingMode('yearly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  pricingMode === 'yearly'
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pay Upfront
              </button>
              <button
                onClick={() => setPricingMode('monthly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  pricingMode === 'monthly'
                    ? 'bg-indigo-600 text-white shadow-lg'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pay Monthly
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Free Trial Card */}
            <div className={`relative p-6 sm:p-8 rounded-2xl border transition-all duration-300 hover:shadow-xl ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700 hover:border-indigo-500/50'
                : 'bg-white border-gray-200 hover:border-indigo-300'
            }`}>
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  theme === 'dark' 
                    ? 'bg-slate-700 text-indigo-400' 
                    : 'bg-indigo-100 text-indigo-600'
                }`}>
                  <Gift className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Free Trial
                  </h3>
                  <div>
                    <span className={`text-2xl sm:text-3xl font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      Free
                    </span>
                    <span className={`text-sm ml-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      14 days
                    </span>
                  </div>
                </div>
              </div>

              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
                {['Unlimited products', 'Advanced POS system', 'Dues & payments', 'Full reporting suite', 'Thermal printing', 'Custom fields', 'Multi-user access', 'Lifetime updates', 'Priority support'].map((feature, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <CheckCircle className={`w-4 h-4 flex-shrink-0 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleOpenContactModal}
                className={`block w-full py-2.5 sm:py-3 text-center text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 hover:shadow-lg active:scale-[0.98] ${
                  theme === 'dark'
                    ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Start Free Trial
              </button>
            </div>

            {/* Complete Package Card */}
            <div className={`relative p-6 sm:p-8 rounded-2xl border-2 transition-all duration-300 shadow-xl ${
              theme === 'dark'
                ? 'bg-indigo-900/30 border-indigo-600'
                : 'bg-white border-indigo-600'
            }`}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-full">
                {pricingMode === 'monthly' ? 'Flexible Payment' : 'Best Value'}
              </div>

              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-indigo-600 text-white">
                  <BadgeCheck className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-bold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Complete Package
                  </h3>
                  <div>
                    <span className={`text-2xl sm:text-3xl font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {pricingMode === 'yearly' ? '₨42,000' : '₨3,500'}
                    </span>
                    <span className={`text-sm ml-2 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {pricingMode === 'yearly' ? 'first year' : '/month'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className={`mb-4 p-3 rounded-xl text-sm ${
                theme === 'dark' 
                  ? 'bg-slate-800/50 border border-slate-700' 
                  : 'bg-indigo-50 border border-indigo-100'
              }`}>
                {pricingMode === 'yearly' ? (
                  <div className="flex justify-between gap-4">
                    <div className="flex justify-between flex-1">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>License (lifetime)</span>
                      <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>₨30,000</span>
                    </div>
                    <div className={`w-px ${theme === 'dark' ? 'bg-slate-600' : 'bg-indigo-200'}`}></div>
                    <div className="flex justify-between flex-1">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Hosting (per year)</span>
                      <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>₨12,000</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between gap-4">
                    <div className="flex justify-between flex-1">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>License</span>
                      <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>₨3,500/mo</span>
                    </div>
                    <div className={`w-px ${theme === 'dark' ? 'bg-slate-600' : 'bg-indigo-200'}`}></div>
                    <div className="flex justify-between flex-1">
                      <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Hosting</span>
                      <span className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>₨1,000/mo</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Renewal Price Note */}
              <div className={`mb-4 px-3 py-2 rounded-lg text-xs font-medium text-center ${
                theme === 'dark' 
                  ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-800' 
                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}>
                {pricingMode === 'yearly' 
                  ? 'After 1st year: Only ₨12,000/year for hosting renewal'
                  : 'After 1st year: Only ₨1,000/month for hosting renewal'
                }
              </div>

              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
                {[
                  'Unlimited products',
                  'Advanced POS system',
                  'Dues & payments',
                  'Full reporting suite',
                  'Thermal printing',
                  'Custom fields',
                  'Multi-user access',
                  'Lifetime updates',
                  'Priority support'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0 text-indigo-500" />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={handleOpenContactModal}
                className="block w-full py-2.5 sm:py-3 text-center text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 hover:shadow-lg active:scale-[0.98] bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Get Started Now
              </button>
            </div>
          </div>

          {/* Pricing Explanation */}
          <div className={`mt-10 p-6 sm:p-8 rounded-2xl border-2 ${
            theme === 'dark' 
              ? 'bg-slate-800/50 border-slate-700' 
              : 'bg-slate-50 border-slate-200'
          }`}>
            <h4 className={`text-lg font-bold mb-6 text-center ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              How Our Pricing Works
            </h4>
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Option 1: Pay Upfront */}
              <div className={`p-5 rounded-xl ${
                theme === 'dark' ? 'bg-slate-700/50' : 'bg-white border border-slate-200'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
                    <Banknote className="w-5 h-5" />
                  </div>
                  <h5 className={`font-semibold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Pay Upfront</h5>
                </div>
                <div className={`space-y-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span><strong>₨30,000</strong> one-time for lifetime software license</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span><strong>₨12,000</strong> per year for web hosting</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span>Software is <strong>yours forever</strong> after first payment</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span>After year 1, only pay <strong>₨12,000/year</strong> for hosting</span>
                  </div>
                </div>
              </div>
              
              {/* Option 2: Pay Monthly */}
              <div className={`p-5 rounded-xl ${
                theme === 'dark' ? 'bg-slate-700/50' : 'bg-white border border-slate-200'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
                    <Wallet className="w-5 h-5" />
                  </div>
                  <h5 className={`font-semibold text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Pay Monthly</h5>
                </div>
                <div className={`space-y-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-3">
                    <BadgeCheck className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span><strong>₨3,500/month</strong> for software license</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span><strong>₨1,000/month</strong> for web hosting</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calculator className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <span>Total: <strong>₨3,500/month</strong> — easier on your budget</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span>After year 1, only pay <strong>₨1,000/month</strong> for hosting</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`mt-6 pt-4 border-t text-center ${
              theme === 'dark' ? 'border-slate-600' : 'border-slate-200'
            }`}>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <strong className={theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}>Why pay for hosting?</strong> This is a multi-tenant web platform. Your shop runs on our secure servers with automatic backups, updates, and 24/7 support — no technical setup required on your end!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className={`py-16 sm:py-24 px-4 sm:px-6 lg:px-8 ${
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Frequently Asked Questions
            </h2>
            <p className={`text-base sm:text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Everything you need to know about our POS software and inventory management system
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {faqItems.map((faq, index) => (
              <div
                key={index}
                className={`group rounded-2xl border overflow-hidden transition-all duration-500 ease-out ${
                  theme === 'dark' 
                    ? 'border-slate-700 bg-slate-800/30 hover:bg-slate-800/60' 
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                } ${openFaqIndex === index 
                    ? theme === 'dark' 
                      ? 'shadow-xl shadow-indigo-500/10 border-indigo-500/50' 
                      : 'shadow-xl shadow-indigo-500/10 border-indigo-300'
                    : 'hover:shadow-lg'
                }`}
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full flex items-start gap-4 p-5 text-left transition-all duration-300"
                >
                  {/* Number Badge */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    openFaqIndex === index
                      ? 'bg-indigo-600 text-white scale-110'
                      : theme === 'dark'
                        ? 'bg-slate-700 text-gray-400 group-hover:bg-indigo-600/20 group-hover:text-indigo-400'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-indigo-100 group-hover:text-indigo-600'
                  }`}>
                    {(index + 1).toString().padStart(2, '0')}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <span className={`font-semibold block transition-colors duration-300 ${
                      openFaqIndex === index
                        ? 'text-indigo-600'
                        : theme === 'dark' 
                          ? 'text-white group-hover:text-indigo-400' 
                          : 'text-gray-900 group-hover:text-indigo-600'
                    }`}>
                      {faq.question}
                    </span>
                  </div>
                  
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                    openFaqIndex === index
                      ? 'bg-indigo-600 text-white rotate-180'
                      : theme === 'dark'
                        ? 'bg-slate-700 text-gray-400'
                        : 'bg-gray-100 text-gray-500'
                  }`}>
                    <ChevronDown className="w-4 h-4" />
                  </div>
                </button>
                
                <div 
                  className={`grid transition-all duration-500 ease-out ${
                    openFaqIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className={`px-5 pb-5 pl-[4.25rem] text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Ready to Upgrade Your Retail Business?
          </h2>
          <p className={`text-base sm:text-lg mb-8 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Join hundreds of Pakistani shops using our digital POS system and inventory management software. Start your free 14-day trial today!
          </p>
          <button
            onClick={handleOpenContactModal}
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            Start Your Free Trial
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Contact Modal */}
      {(isContactModalOpen || isModalClosing) && createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${
          isModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
        }`}>
          <div
            className="flex items-center justify-center min-h-full px-4 py-6 text-center"
            onClick={(e) => e.target === e.currentTarget && handleCloseContactModal()}
          >
            {/* Background overlay */}
            <div
              className={`fixed inset-0 transition-opacity duration-300 ease-out ${
                isModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
              onClick={handleCloseContactModal}
            />

            {/* Modal panel */}
            <div className={`inline-block w-full max-w-lg p-5 sm:p-8 overflow-hidden text-left align-middle transform shadow-2xl rounded-2xl transition-all duration-300 ${
              isModalClosing ? 'animate-scaleOut' : 'animate-scaleIn'
            } ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
                    <Send className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg sm:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Get Started Today
                    </h3>
                    <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Contact us for license or demo
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseContactModal}
                  className={`p-2 rounded-xl transition-colors ${
                    theme === 'dark' ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Tab Navigation */}
              <div className={`flex mb-5 sm:mb-6 p-1 rounded-xl ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                    activeTab === 'contact'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-white hover:bg-slate-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Quick</span> Contact
                </button>
                <button
                  onClick={() => setActiveTab('form')}
                  className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                    activeTab === 'form'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-white hover:bg-slate-800'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline">Send</span> Message
                </button>
              </div>

              {activeTab === 'contact' ? (
                <>
              {/* Contact Options */}
              <div className="space-y-3">
                {/* WhatsApp */}
                <a
                  href="https://wa.me/923062169608?text=Hi!%20I'm%20interested%20in%20NextGen%20-%20Shop%20license.%20Please%20share%20more%20details."
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 group ${
                    theme === 'dark'
                      ? 'bg-slate-900/50 border-slate-700 hover:border-green-500 hover:bg-green-900/20'
                      : 'bg-gray-50 border-gray-200 hover:border-green-500 hover:bg-green-50'
                  }`}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                    <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                    <p className={`font-semibold text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      WhatsApp
                    </p>
                    <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Chat with us instantly
                    </p>
                  </div>
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 group-hover:translate-x-1 ${
                    theme === 'dark' ? 'text-gray-500 group-hover:text-green-400' : 'text-gray-400 group-hover:text-green-600'
                  }`} />
                </a>

                {/* Phone Call */}
                <a
                  href="tel:+923062169608"
                  className={`flex items-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 group ${
                    theme === 'dark'
                      ? 'bg-slate-900/50 border-slate-700 hover:border-indigo-500 hover:bg-indigo-900/20'
                      : 'bg-gray-50 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'
                  }`}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                    <p className={`font-semibold text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Call Us
                    </p>
                    <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      +92 306 216 9608
                    </p>
                  </div>
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 group-hover:translate-x-1 ${
                    theme === 'dark' ? 'text-gray-500 group-hover:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-600'
                  }`} />
                </a>

                {/* Email */}
                <a
                  href="mailto:info@hindukushsoft.com?subject=Inquiry%20about%20NextGen%20-%20Shop%20License"
                  className={`flex items-center p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 group ${
                    theme === 'dark'
                      ? 'bg-slate-900/50 border-slate-700 hover:border-indigo-500 hover:bg-indigo-900/20'
                      : 'bg-gray-50 border-gray-200 hover:border-indigo-500 hover:bg-indigo-50'
                  }`}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                    <p className={`font-semibold text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Email Us
                    </p>
                    <p className={`text-xs sm:text-sm truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      info@hindukushsoft.com
                    </p>
                  </div>
                  <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-300 group-hover:translate-x-1 ${
                    theme === 'dark' ? 'text-gray-500 group-hover:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-600'
                  }`} />
                </a>
              </div>

              {/* Footer Info */}
              <div className={`mt-5 sm:mt-6 pt-5 sm:pt-6 border-t ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-center space-x-2">
                  <MapPin className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Based in Pakistan • Serving Worldwide
                  </p>
                </div>
                <p className={`text-xs text-center mt-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                  We typically respond within 1-2 hours
                </p>
              </div>
                </>
              ) : (
                /* Contact Form */
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {formStatus === 'success' ? (
                    <div className={`p-6 rounded-xl text-center ${
                      theme === 'dark' ? 'bg-green-900/30 border border-green-800' : 'bg-green-50 border border-green-200'
                    }`}>
                      <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        Message Sent!
                      </h4>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {formMessage}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setFormStatus('idle'); setActiveTab('contact'); }}
                        className="mt-4 text-indigo-500 hover:text-indigo-600 text-sm font-medium"
                      >
                        Back to contact options
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Two Column Layout */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Your Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter your name"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 ${
                              theme === 'dark'
                                ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>
                        <div>
                          <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            Email Address *
                          </label>
                          <input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="your@email.com"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 ${
                              theme === 'dark'
                                ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                                : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+92 XXX XXX XXXX"
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 ${
                            theme === 'dark'
                              ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Your Message *
                        </label>
                        <textarea
                          required
                          rows={2}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          placeholder="Tell us about your business and requirements..."
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none ${
                            theme === 'dark'
                              ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }`}
                        />
                      </div>

                      {formStatus === 'error' && (
                        <div className={`p-3 rounded-xl text-sm ${
                          theme === 'dark' ? 'bg-red-900/30 text-red-400 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'
                        }`}>
                          {formMessage}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-2.5 sm:py-3 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg active:scale-[0.98]"
                      >
                        {formStatus === 'loading' ? (
                          <>
                            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                            Send Message
                          </>
                        )}
                      </button>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Feature Request Modal */}
      {(isFeatureRequestModalOpen || isFeatureModalClosing) && createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${
          isFeatureModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
        }`}>
          <div
            className="flex items-center justify-center min-h-full px-4 py-6 text-center"
            onClick={(e) => e.target === e.currentTarget && handleCloseFeatureRequestModal()}
          >
            <div
              className={`fixed inset-0 transition-opacity duration-300 ease-out ${
                isFeatureModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
              onClick={handleCloseFeatureRequestModal}
            />

            <div className={`inline-block w-full max-w-lg p-5 sm:p-8 overflow-hidden text-left align-middle transform shadow-2xl rounded-2xl transition-all duration-300 ${
              isFeatureModalClosing ? 'animate-scaleOut' : 'animate-scaleIn'
            } ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
              {/* Header */}
              <div className="flex items-center justify-between mb-5 sm:mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-lg sm:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Request a Feature
                    </h3>
                    <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Help us improve {SITE_NAME}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseFeatureRequestModal}
                  className={`p-2 rounded-xl transition-colors ${
                    theme === 'dark' ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleFeatureFormSubmit} className="space-y-4">
                {featureFormStatus === 'success' ? (
                  <div className={`p-6 rounded-xl text-center ${
                    theme === 'dark' ? 'bg-green-900/30 border border-green-800' : 'bg-green-50 border border-green-200'
                  }`}>
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Thank You!
                    </h4>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {featureFormMessage}
                    </p>
                    <button
                      type="button"
                      onClick={handleCloseFeatureRequestModal}
                      className="mt-4 text-indigo-500 hover:text-indigo-600 text-sm font-medium"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Your Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={featureFormData.name}
                          onChange={(e) => setFeatureFormData({ ...featureFormData, name: e.target.value })}
                          placeholder="Enter your name"
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 ${
                            theme === 'dark'
                              ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          value={featureFormData.email}
                          onChange={(e) => setFeatureFormData({ ...featureFormData, email: e.target.value })}
                          placeholder="your@email.com"
                          className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 ${
                            theme === 'dark'
                              ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                          }`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        Feature Description *
                      </label>
                      <textarea
                        required
                        rows={3}
                        value={featureFormData.feature}
                        onChange={(e) => setFeatureFormData({ ...featureFormData, feature: e.target.value })}
                        placeholder="Describe the feature you'd like to see..."
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm rounded-xl border-2 transition-all duration-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 resize-none ${
                          theme === 'dark'
                            ? 'bg-slate-900 border-slate-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                        }`}
                      />
                    </div>

                    {featureFormStatus === 'error' && (
                      <div className={`p-3 rounded-xl text-sm ${
                        theme === 'dark' ? 'bg-red-900/30 text-red-400 border border-red-800' : 'bg-red-50 text-red-600 border border-red-200'
                      }`}>
                        {featureFormMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={featureFormStatus === 'loading'}
                      className="w-full py-3 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      {featureFormStatus === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Lightbulb className="w-5 h-5" />
                          Submit Feature Request
                        </>
                      )}
                    </button>

                    <p className={`text-xs text-center ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                      We review all feature requests and prioritize based on user demand.
                    </p>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Footer */}
      <footer className={`py-8 sm:py-12 md:py-10 px-4 sm:px-6 lg:px-8 border-t ${
        theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 md:gap-4">
            {/* Logo Section */}
            <div className="flex flex-col items-center md:items-start">
              <div className="flex items-center space-x-2 mb-2">
                {getLogoUrl() ? (
                  <img 
                    src={getLogoUrl()} 
                    alt={SITE_NAME}
                    className="w-8 h-8 rounded-lg object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    theme === 'dark' ? 'bg-indigo-600' : 'bg-indigo-600'
                  }`}>
                    <Store className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className={`font-semibold text-sm sm:text-base ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {SITE_NAME}
                </span>
              </div>
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>
                © {new Date().getFullYear()} All rights reserved.
              </p>
            </div>

            {/* Center Section - Powered by */}
            <div className="text-center">
              <p className={`text-xs sm:text-sm ${
                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>
                Proudly powered by{' '}
                <a 
                  href="https://www.hindukushsoft.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-500 hover:text-indigo-600 transition-colors duration-300 font-medium"
                >
                  HindukushSoft Technologies
                </a>
              </p>
            </div>
            
            {/* Links Section */}
            <div className="flex items-center justify-center md:justify-end space-x-6">
              <button 
                onClick={handleOpenPrivacyModal}
                className={`text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                  theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Privacy
              </button>
              <button 
                onClick={handleOpenTermsModal}
                className={`text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                  theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Terms
              </button>
              <button 
                onClick={handleOpenContactModal}
                className={`text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                  theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {(isPrivacyModalOpen || (isLegalModalClosing && isPrivacyModalOpen)) && createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${
          isLegalModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
        }`}>
          <div
            className="flex items-center justify-center min-h-full px-4 py-6 text-center"
            onClick={(e) => e.target === e.currentTarget && handleCloseLegalModal()}
          >
            <div
              className={`fixed inset-0 transition-opacity duration-300 ease-out ${
                isLegalModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
              onClick={handleCloseLegalModal}
            />

            <div className={`inline-block w-full max-w-3xl max-h-[85vh] overflow-hidden text-left align-middle transform shadow-2xl rounded-2xl ${
              isLegalModalClosing ? 'animate-scaleOut' : 'animate-scaleIn'
            } ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
              {/* Header */}
              <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
                theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Privacy Policy
                    </h3>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Last updated: December 2025
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseLegalModal}
                  className={`p-2 rounded-xl transition-colors ${
                    theme === 'dark' ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className={`p-6 overflow-y-auto max-h-[calc(85vh-80px)] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="space-y-6">
                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      1. Information We Collect
                    </h4>
                    <p className="mb-3">
                      {SITE_NAME} collects information necessary to provide our inventory management and POS services:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Account Information:</strong> Business name, email address, phone number, and login credentials when you register.</li>
                      <li><strong>Business Data:</strong> Product inventory, customer records, sales transactions, supplier information, and purchase orders you enter into the system.</li>
                      <li><strong>Usage Data:</strong> How you interact with our platform, including features used, pages visited, and system preferences.</li>
                      <li><strong>Device Information:</strong> Browser type, IP address, and device identifiers for security and optimization purposes.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      2. How We Use Your Information
                    </h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Service Delivery:</strong> To provide inventory management, POS, sales tracking, customer management, and reporting features.</li>
                      <li><strong>Communication:</strong> To send transactional emails, password resets, and important system updates.</li>
                      <li><strong>Improvement:</strong> To analyze usage patterns and improve our platform's features and performance.</li>
                      <li><strong>Support:</strong> To respond to your inquiries and provide technical assistance.</li>
                      <li><strong>Security:</strong> To protect against unauthorized access, fraud, and ensure data integrity.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      3. Data Storage & Security
                    </h4>
                    <p className="mb-3">
                      Your data is stored securely on cloud servers with industry-standard encryption. We implement:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>SSL/TLS encryption for all data transmission</li>
                      <li>Encrypted database storage for sensitive information</li>
                      <li>Regular automated backups</li>
                      <li>Role-based access controls within your organization</li>
                      <li>Secure authentication with token-based sessions</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      4. Data Sharing
                    </h4>
                    <p className="mb-3">
                      We do <strong>not</strong> sell your business data. We may share information only in these cases:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>With Your Consent:</strong> When you explicitly authorize sharing.</li>
                      <li><strong>Service Providers:</strong> Trusted partners who help operate our platform (hosting, email delivery) under strict confidentiality agreements.</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect rights and safety.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      5. Your Rights
                    </h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Access:</strong> Request a copy of your stored data at any time.</li>
                      <li><strong>Correction:</strong> Update or correct inaccurate information.</li>
                      <li><strong>Deletion:</strong> Request deletion of your account and associated data.</li>
                      <li><strong>Export:</strong> Export your business data in standard formats (PDF, Excel).</li>
                      <li><strong>Opt-out:</strong> Disable optional notifications and marketing communications.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      6. Data Retention
                    </h4>
                    <p>
                      We retain your data for as long as your account is active. Upon account termination, data is securely deleted within 30 days, unless retention is required by law or for legitimate business purposes (e.g., backup archives).
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      7. Contact Us
                    </h4>
                    <p>
                      For privacy-related inquiries, contact us at{' '}
                      <a href="mailto:info@hindukushsoft.com" className="text-indigo-500 hover:text-indigo-600">
                        info@hindukushsoft.com
                      </a>{' '}
                      or call{' '}
                      <a href="tel:+923062169608" className="text-indigo-500 hover:text-indigo-600">
                        +92 306 216 9608
                      </a>.
                    </p>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Terms of Service Modal */}
      {(isTermsModalOpen || (isLegalModalClosing && isTermsModalOpen)) && createPortal(
        <div className={`fixed inset-0 z-50 overflow-y-auto ${
          isLegalModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
        }`}>
          <div
            className="flex items-center justify-center min-h-full px-4 py-6 text-center"
            onClick={(e) => e.target === e.currentTarget && handleCloseLegalModal()}
          >
            <div
              className={`fixed inset-0 transition-opacity duration-300 ease-out ${
                isLegalModalClosing ? 'animate-fadeOut' : 'animate-fadeIn'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)'
              }}
              onClick={handleCloseLegalModal}
            />

            <div className={`inline-block w-full max-w-3xl max-h-[85vh] overflow-hidden text-left align-middle transform shadow-2xl rounded-2xl ${
              isLegalModalClosing ? 'animate-scaleOut' : 'animate-scaleIn'
            } ${theme === 'dark' ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'}`}>
              {/* Header */}
              <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${
                theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Terms of Service
                    </h3>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Last updated: December 2025
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseLegalModal}
                  className={`p-2 rounded-xl transition-colors ${
                    theme === 'dark' ? 'hover:bg-slate-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className={`p-6 overflow-y-auto max-h-[calc(85vh-80px)] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className="space-y-6">
                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      1. Acceptance of Terms
                    </h4>
                    <p>
                      By accessing or using {SITE_NAME} ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service. These terms apply to all users, including shop owners, administrators, and staff members.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      2. Service Description
                    </h4>
                    <p className="mb-3">
                      {SITE_NAME} is a comprehensive inventory management and Point of Sale (POS) system that provides:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Product and inventory management with stock tracking</li>
                      <li>Point of Sale (POS) with receipt printing</li>
                      <li>Customer management and dues tracking</li>
                      <li>Sales and purchase order management</li>
                      <li>Supplier management</li>
                      <li>Comprehensive reporting and analytics</li>
                      <li>Multi-user access with role-based permissions</li>

                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      3. Subscription & Pricing
                    </h4>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong>Free Trial:</strong> New users receive a 14-day free trial with full access to all features.</li>
                      <li><strong>License Fee:</strong> One-time payment of Rs 30,000 for lifetime software access.</li>
                      <li><strong>Web Hosting:</strong> Rs 12,000 per year for web hosting, backups, and maintenance.</li>
                      <li><strong>Self-Hosting:</strong> You may host the software on your own servers after purchasing the license.</li>
                      <li><strong>Refunds:</strong> License fees are non-refundable after 7 days of purchase. Hosting fees are non-refundable.</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      4. User Responsibilities
                    </h4>
                    <p className="mb-3">As a user, you agree to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide accurate business and account information</li>
                      <li>Maintain the security of your login credentials</li>
                      <li>Use the Service only for lawful business purposes</li>
                      <li>Not share your account with unauthorized parties</li>
                      <li>Ensure your staff members comply with these terms</li>
                      <li>Maintain accurate records and comply with local tax regulations</li>
                      <li>Not attempt to reverse-engineer, copy, or redistribute the software</li>
                    </ul>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      5. Account Suspension
                    </h4>
                    <p className="mb-3">We may suspend or terminate your account if:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Your subscription or hosting payment is overdue</li>
                      <li>You violate these Terms of Service</li>
                      <li>Fraudulent or illegal activity is detected</li>
                      <li>Your trial period expires without subscription</li>
                    </ul>
                    <p className="mt-3">
                      Upon suspension, you will retain read-only access to export your data for 30 days.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      6. Data Ownership
                    </h4>
                    <p>
                      You retain full ownership of all business data you enter into {SITE_NAME}, including products, customers, sales, and reports. We do not claim any ownership rights to your data. You may export your data at any time using built-in export features.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      7. Service Availability
                    </h4>
                    <p>
                      We strive for 99.9% uptime for cloud-hosted accounts. However, we do not guarantee uninterrupted service and are not liable for downtime due to maintenance, updates, or circumstances beyond our control. Scheduled maintenance will be communicated in advance when possible.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      8. Limitation of Liability
                    </h4>
                    <p>
                      {SITE_NAME} is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from use of the Service. Our total liability is limited to the amount paid for your subscription in the past 12 months.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      9. Updates & Modifications
                    </h4>
                    <p>
                      We may update features, pricing, and these terms from time to time. Significant changes will be communicated via email or in-app notification. Continued use of the Service after changes constitutes acceptance of the modified terms.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      10. Governing Law
                    </h4>
                    <p>
                      These terms are governed by the laws of Pakistan. Any disputes shall be resolved through arbitration in Pakistan before pursuing legal action.
                    </p>
                  </section>

                  <section>
                    <h4 className={`text-lg font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      11. Contact Information
                    </h4>
                    <p>
                      For questions about these terms, contact us at{' '}
                      <a href="mailto:info@hindukushsoft.com" className="text-indigo-500 hover:text-indigo-600">
                        info@hindukushsoft.com
                      </a>{' '}
                      or call{' '}
                      <a href="tel:+923062169608" className="text-indigo-500 hover:text-indigo-600">
                        +92 306 216 9608
                      </a>.
                    </p>
                  </section>

                  <div className={`mt-8 p-4 rounded-xl ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-100'}`}>
                    <p className={`text-sm text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      By using {SITE_NAME}, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LandingPage;

